import {useCallback, useEffect, useRef, useState} from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteLocal, getActions, getLocal, INITIAL_PROFILE, ONBOARDING_SLIDES, sendAction, setLocal, updateProfile } from '../../utils/api';

import './Onboarding.css';

function Onboarding({ handleConfetti, handleError }) {
  const navigate = useNavigate();
  const refs = useRef({});

  const [isSending, setIsSending] = useState(false);
  const [profile, setProfile] = useState(getLocal('profile'));
  const [selectedOptions, setSelectedOptions] = useState({});
  const [slideActive, setSlideActive] = useState(null);
  const [slides, setSlides] = useState([...ONBOARDING_SLIDES]);
  
  const checkProgress = useCallback(async () => {
    let actions = await getActions('onboarding');

    if (actions?.length) {
      if (profile?.meta_data) {
        const slides = INITIAL_PROFILE.filter(item => item.category === 'other');
        let activeSlide;

        for (let slide of slides) {
          const item = profile.meta_data.find(item => item.param === slide.param);

          if (!item) {
            activeSlide = slide.param;
            break;
          }
        }

        if (activeSlide) {
          setLocal('slide', activeSlide);
          setSlideActive(activeSlide);
        } else {
          navigate('/path');
        }
      } else {
        setLocal('slide', 'welcome');
        setSlideActive('welcome');
      }
    } else {
      const actions = await getActions('disclosure');

      if (actions?.length) {
        const slide = getLocal('slide');

        if (slide) {
          setSlideActive(slide);
        } else {
          setLocal('slide', 'name');
          setSlideActive('name');
        }
      } else {
        setLocal('slide', 'welcome');
        setSlideActive('welcome');
      }
    }
  }, [navigate]);

  useEffect(() => {
    checkProgress();
  }, [checkProgress]);

  const submitParam = useCallback(async (slide, value, next) => {
    setIsSending(true);
    
    try {
      const response = await updateProfile(slide.param, value);

      if (response !== null) {
        setProfile(response);
        
        if (slide.next === 'path') {
          await sendAction('onboarding');

          deleteLocal('slide');

          handleConfetti();
          navigate('/path');
        } else if (next || slide.next) {
          setLocal('slide', next || slide.next);
          setSlideActive(next || slide.next);
        }
      }
    } catch (e) {
      handleError(e);
    }

    setIsSending(false);
  }, [handleConfetti, handleError, navigate]);

  const handleSelect = useCallback(async (slide, option) => {
    const value = (option.value || option.label);

    if (slide.input === 'multiselect' || slide.input === 'select') {
      setSelectedOptions(prevSelectedOptions => {
        if (slide.param in prevSelectedOptions) {
          const optionIndex = prevSelectedOptions[slide.param].indexOf(value);

          if (optionIndex >= 0) {
            if (slide.input === 'multiselect') {
              prevSelectedOptions[slide.param].splice(optionIndex, 1);
            } else {
              prevSelectedOptions[slide.param] = [];
            }
          } else {
            if (slide.input === 'multiselect') {
              prevSelectedOptions[slide.param].push(value);
            } else {
              prevSelectedOptions[slide.param] = [value];
            }
          }
        } else {
          prevSelectedOptions[slide.param] = [value];
        }

        return {...prevSelectedOptions};
      });
    } else {
      await submitParam(slide, value, option.next);
    }
  }, [submitParam]);

  const handleSlide = useCallback(async (slide) => {
    let value;

    if (slide.input === 'multiselect' || slide.input === 'select') {
      let values = [];

      if (slide.param in selectedOptions) {
        values = selectedOptions[slide.param];
      }

      if ((slide.input === 'multiselect' || !values.length) && slide.param in refs.current && refs.current[slide.param].value) {
        values.push(refs.current[slide.param].value);
      }

      value = values;
    } else if ((slide.input === 'text' || slide.input === 'textarea') && slide.param in refs.current) {
      value = refs.current[slide.param].value;
    } else {
      value = null;
    }

    if (!slide.param || slide.canSkip || value?.length) {
      if (slide.param) {
        await submitParam(slide, (value?.length ? value : null), value?.next);
      }
      
      if (slide.sendAction) {
        try {
          await sendAction(slide.sendAction);

          handleConfetti();
        } catch (e) {
          handleError(e);
        }
      }

      if (!slide.param && slide.next) {
        setLocal('slide', slide.next)
        setSlideActive(slide.next);
      }
    }
  }, [handleConfetti, handleError, selectedOptions, submitParam]);

  const handleSubmit = useCallback(async (slide, keyEvent) => {
    let value;

    if (keyEvent) {
      if (keyEvent.keyCode === 13) {
        value = keyEvent.target.value;
      }
    } else {
      value = refs.current[slide.param].value;
    }

    if (value) {
      if (slide.input === 'multiselect') {
        setSlides(prevSlides => {
          const slideIndex = prevSlides.findIndex(prevSlide => prevSlide.param === slide.param);

          if (slideIndex >= 0) {
            const optionIndex = prevSlides[slideIndex].values.findIndex(option => option.label === value);

            if (optionIndex === -1) {
              prevSlides[slideIndex].values.push({ label: value });
            }
          }

          return [...prevSlides];
        });

        handleSelect(slide, { label: value });

        refs.current[slide.param].value = '';
      } else {
        await submitParam(slide, value, value.next);
      }
    }
  }, [handleSelect, submitParam]);

  useEffect(() => {
    if (!profile?.meta_data?.length) {
      return;
    }

    const initialOptions = {};

    profile.meta_data.forEach(item => {
      if (Array.isArray(item.value) && item.value.length) {
        initialOptions[item.param] = [...item.value];
      }
    });

    if (!Object.keys(initialOptions).length) {
      return;
    }

    setSelectedOptions(initialOptions);

    setSlides(prevSlides => prevSlides.map(slide => {
      if ((slide.input === 'multiselect' || slide.input === 'select') && initialOptions[slide.param]) {
        const knownValues = slide.values.map(option => option.value || option.label);
        const customLabels = initialOptions[slide.param].filter(value => !knownValues.includes(value));

        if (customLabels.length) {
          return {
            ...slide,
            values: [...slide.values, ...customLabels.map(label => ({ label }))]
          };
        }
      }

      return slide;
    }));
  }, []);
/*
  useEffect(() => {
    if (slide <= slides.length - 1 && slides[slide] in refs?.current) {
      setTimeout(() => {
        refs.current[slides[slide]].focus();
      }, 100);
    }
  }, [slide, slides]);
*/
  return (
    <div id="onboarding">
      {slides.map((slide, slideIndex) => {
        const profileItem = profile?.meta_data?.find(item => item.param === slide.param);
        const initialValue = typeof profileItem?.value === 'string' ? profileItem.value : '';

        return (
          <div key={slide.id} className={'slide' + (slide.id === slideActive ? ' active' : '')}>
            <div className="header">
              <div className="clamp">
                {slide.prev ? (
                  <div className="back" onClick={() => setSlideActive(slide.prev)}></div>
                ) : null}
                <div className="progress-container">
                  <div className="progress" style={{ '--slide-count': (slides.length - 1), '--slide-index': slideIndex }}></div>
                </div>
              </div>
            </div>
            <div className="body overflow">
              <div className="clamp">
                {slide.type === 'card' ? (
                  <div className="card-container">
                    <div className="card-container-header">
                      <div className="card-container-title">Pick what feels true for you right now</div>
                      <div className="card-container-counter">{slides.filter(slide => slide.type === 'card').findIndex(slide => slide.id === slideActive) + 1} of {slides.filter(slide => slide.type === 'card').length}</div>
                    </div>
                    <div className="card">
                      <div className="card-header">
                        <div className={'card-icon' + (slide.icon ? ' ' + slide.icon : '')}></div>
                      </div>
                      <div className="card-body">
                        <div className="card-title">{slide.title}</div>
                        {slide.subtitle ? (
                          <div className="card-subtitle">{slide.subtitle}</div>
                        ) : null}
                        {slide.pills?.length ? (
                          <div className="pills">
                            {slide.pills.map((pill, pillIndex) =>
                              <div key={pillIndex} className="pill pill-theme-2">{pill}</div>
                            )}
                          </div>
                        ) : null}
                        <div className="actions">
                          <div className="action action-secondary" onClick={() => submitParam(slide, false)}>Not really</div>
                          <div className="action action-primary" onClick={() => submitParam(slide, true)}>Yes, this is me</div>
                        </div>
                      </div>
                      <div className="card-footer">
                        <div className="action action-secondary" onClick={() => submitParam(slide, null)}>Skip for now</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="aiguide-left">
                      <div className="aiguide"></div>
                      {slide.bubbles?.length ? (
                        <div className="aiguide-bubbles">
                          {slide.bubbles.map((bubble, bubbleIndex) =>
                            <div key={bubbleIndex} className="aiguide-bubble">{bubble}</div>
                          )}
                        </div>
                      ) : null}
                    </div>
                    {(slide.input === 'multiselect' || slide.input === 'select') && slide.values.length ? (
                      <div className="options">
                        {slide.values.map((option, optionIndex) =>
                          <div key={optionIndex} className={'option' + (slide.param in selectedOptions && selectedOptions[slide.param].indexOf(option.value || option.label) >= 0 ? ' selected': '')} onClick={() => handleSelect(slide, option)}>
                            <div className="option-label">{option.label}</div>
                            {option.sublabel ? (
                              <div className="option-sublabel">{option.sublabel}</div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
            <div className="footer clamp">
              {slide.showPrivacy ? (
                <div className="privacy">Your information is private. You control what others can see.</div>
              ) : null}
              {slide.param && slide.category && slide.input && slide.placeholder ? (
                <div className="text-input">
                  {slide.input === 'textarea' ? (
                    <textarea ref={element => refs.current[slide.param] = element} defaultValue={initialValue} placeholder={isSending ? 'Give me a sec...' : (slide.placeholder || '')} autoComplete="off" disabled={isSending} />
                  ) : (
                    <>
                      <input ref={element => refs.current[slide.param] = element} type="input" defaultValue={initialValue} placeholder={isSending ? 'Give me a sec...' : (slide.placeholder || '')} autoComplete="off" disabled={isSending}/* onKeyDown={(e) => handleSubmit(slide, e)}*/ />
                      {/*<div className="text-submit" onClick={() => handleSubmit(slide)}></div>*/}
                    </>
                  )}
                </div>
              ) : null}
              {slide.action ? (
                <div className="action action-primary" onClick={() => handleSlide(slide)}>{slide.action}</div>
              ) : null}
              {slide.canSkip ? (
                <div className="skip" onClick={() => submitParam(slide, null)}>Skip for now</div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Onboarding;
